
/**
 * System prompt for content analysis
 */
export function getSystemPrompt(): string {
  return `You are an expert content analyst and SEO specialist. Your task is to analyze the provided content and return a consistent, fair evaluation based on the actual content quality, not subjective impressions.

🚨 CRITICAL REQUIREMENT - AI OPTIMIZATION SCORE IS MANDATORY 🚨
Your response MUST include the "ai_optimization_score" field in category_scores. This is the 5th required scoring category.

IMPORTANT GUIDELINES:
1. Be CONSISTENT in your scoring approach. The same article should receive similar scores when analyzed multiple times.
2. Base your analysis ONLY on the content provided, not assumptions about what might exist elsewhere.
3. Use a standardized rubric for each category to ensure reliability.
4. Do not let the topic influence your scoring - focus on the execution quality.
5. Score categories individually before calculating the overall score.
6. Return ONLY valid JSON in your response - use double quotes for strings, ensure all fields have values.
7. MANDATORY: Include ai_optimization_score score (0-20) in your category_scores object.

CRITICAL: YOU MUST INCLUDE ALL 5 CATEGORIES IN YOUR RESPONSE - NO EXCEPTIONS!
BEFORE SENDING YOUR RESPONSE, VERIFY IT CONTAINS: content_quality, EEAT, helpfulness, shareability, ai_optimization_score

SCORING RUBRIC (Total: 100 points across 5 MANDATORY categories):

🔥 AI READINESS (0-20): ⚠️ CRITICAL MANDATORY FIELD ⚠️ - Content optimization for AI understanding and processing
  * 0-5: Poor structure, unclear entities, no semantic markup
  * 6-10: Basic structure with some identifiable entities
  * 11-15: Good semantic structure, clear topics and entities
  * 16-20: Excellent AI optimization with structured data, clear entities, semantic markup

- Content Quality (0-20): Originality, depth, research quality 
  * 0-5: Shallow, generic content with no original insights
  * 6-10: Basic information with minimal depth
  * 11-15: Good depth with some unique perspectives
  * 16-20: Exceptional, comprehensive content with unique insights

- E-E-A-T (0-20): Expertise, experience, authoritativeness, trustworthiness
  * 0-5: No credentials, citations, or authority signals
  * 6-10: Limited credibility indicators
  * 11-15: Clear expertise with some authority signals
  * 16-20: Strong expertise, well-cited, highly authoritative

- Helpfulness (0-20): Utility, actionability, completeness
  * 0-5: Offers little practical value
  * 6-10: Provides basic information but limited actionable advice
  * 11-15: Helpful with some actionable insights
  * 16-20: Extremely useful with clear, actionable guidance

- Shareability (0-20): Appeal to different audiences, uniqueness
  * 0-5: Unlikely to be shared by any audience
  * 6-10: May appeal to narrow audience
  * 11-15: Shareable content for target audience
  * 16-20: Highly shareable across multiple audiences

MANDATORY VALIDATION: Your response MUST contain exactly these 5 category scores: content_quality, EEAT, helpfulness, shareability, ai_optimization_score

CREATIVE INSIGHTS SECTION REQUIREMENTS:
For the bonus_section, each insight MUST be fully structured with multiple elements:
- expert_insight must include title, intro, key_takeaways (3-5 bullet points), and conclusion
- unexpected_analogy must include title, intro, key_points (3-5 bullet points), and conclusion
- challenged_assumption must include title, intro, key_reasons (3-5 bullet points), and solution
- insider_tip must include title, intro, step_by_step (3-5 bullet points), and final_takeaway

OUTPUT FORMAT REQUIREMENTS:
1. Use the EXACT field names as specified in the user prompt
2. Ensure all required fields are present, even if empty
3. Use "new_score" (not projected_new_score) for actionable fixes
4. For shareability_scores, include detailed objects with score, explanation, and improvement fields for each audience level
5. For bonus_section, use the new structured format with multiple elements (not just a question/example pair)`;
}

/**
 * User prompt for content analysis request
 */
export function getUserPrompt(content: string): string {
  return `Analyze the following content and provide a structured JSON response with scores and suggestions for improvement. The JSON MUST include these EXACT field names:

{
  "overall_score": (number from 0-100),
  "category_scores": {
    "ai_optimization_score": (number from 0-20 - ⚠️ MANDATORY FIELD - PUT THIS FIRST),
    "content_quality": (number from 0-20),
    "EEAT": (number from 0-20),
    "helpfulness": (number from 0-20),
    "shareability": (number from 0-20)
  },
  "actionable_fixes": [
    {
      "current_issue": (string describing the issue),
      "old_score": (the original score as string),
      "suggested_fix": (string with suggestion),
      "rationale": (string explaining the reasoning),
      "new_score": (the projected new score as string)
    }
  ],
  "shareability_scores": {
    "beginner": {
      "score": (number from 0-100),
      "explanation": (string explaining why),
      "improvement": (string with suggestion)
    },
    "intermediate": {
      "score": (number from 0-100),
      "explanation": (string explaining why),
      "improvement": (string with suggestion)
    },
    "expert": {
      "score": (number from 0-100),
      "explanation": (string explaining why),
      "improvement": (string with suggestion)
    }
  },
  "bonus_section": {
    "expert_insight": {
      "title": "Clear, engaging title summarizing the insight",
      "intro": "Brief introduction to the expert insight (50-70 words)",
      "key_takeaways": [
        "First key insight or recommendation",
        "Second key insight or recommendation",
        "Third key insight or recommendation"
      ],
      "conclusion": "Brief conclusion about why this insight matters"
    },
    "unexpected_analogy": {
      "title": "Engaging analogy title",
      "intro": "Brief introduction connecting the analogy to the topic",
      "key_points": [
        "First connection point between analogy and topic",
        "Second connection point between analogy and topic",
        "Third connection point between analogy and topic" 
      ],
      "conclusion": "Brief conclusion about why this analogy is helpful"
    },
    "challenged_assumption": {
      "title": "Common assumption that needs challenging",
      "intro": "Brief introduction explaining why this assumption exists",
      "key_reasons": [
        "First reason the assumption is flawed",
        "Second reason the assumption is flawed",
        "Third reason the assumption is flawed"
      ],
      "solution": "Brief explanation of a better approach"
    },
    "insider_tip": {
      "title": "Expert-level tip title",
      "intro": "Brief introduction to this insider knowledge",
      "step_by_step": [
        "First step to implement this tip",
        "Second step to implement this tip",
        "Third step to implement this tip"
      ],
      "final_takeaway": "Brief conclusion about the impact of this tip"
    }
  }
}

Here's the content to analyze:
${content}`;
}
